import { ThemeProvider } from "next-themes";
import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { StorefrontLayout } from "@/components/storefront/StorefrontLayout";
import { BasketProvider } from "@/context/BasketContext";
import { SiteProvider } from "@/context/SiteContext";
import About from "@/pages/About";
import Basket from "@/pages/Basket";
import Checkout from "@/pages/Checkout";
import Contact from "@/pages/Contact";
import Delivery from "@/pages/Delivery";
import Faqs from "@/pages/Faqs";
import Funerals from "@/pages/Funerals";
import Gallery from "@/pages/Gallery";
import Home from "@/pages/Home";
import OrderConfirmation from "@/pages/OrderConfirmation";
import ProductDetail from "@/pages/ProductDetail";
import Shop from "@/pages/Shop";
import Testimonials from "@/pages/Testimonials";
import Weddings from "@/pages/Weddings";
import AdminBranding from "@/pages/admin/AdminBranding";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminContent from "@/pages/admin/AdminContent";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminFaqs from "@/pages/admin/AdminFaqs";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminAdmins from "@/pages/admin/AdminAdmins";
import AdminSetPassword from "@/pages/admin/AdminSetPassword";
import AdminLandingEditor from "@/pages/admin/AdminLandingEditor";
import AdminOrderDetail from "@/pages/admin/AdminOrderDetail";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminProductForm from "@/pages/admin/AdminProductForm";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminTestimonials from "@/pages/admin/AdminTestimonials";
import Dashboard from "@/pages/admin/Dashboard";
import "@/App.css";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function NotFound() {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4" data-testid="not-found-page">
      <p className="font-display text-5xl">404</p>
      <p className="text-muted-foreground mt-3">Sorry, we couldn't find that page.</p>
      <a href="/" className="mt-6 text-primary hover:underline" data-testid="not-found-home-link">Back to home</a>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <SiteProvider>
        <BasketProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route element={<StorefrontLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/weddings" element={<Weddings />} />
                <Route path="/funeral-tributes" element={<Funerals />} />
                <Route path="/delivery" element={<Delivery />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/testimonials" element={<Testimonials />} />
                <Route path="/faqs" element={<Faqs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/basket" element={<Basket />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
              </Route>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/set-password" element={<AdminSetPassword />} />
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Dashboard />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="orders/:id" element={<AdminOrderDetail />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="products/new" element={<AdminProductForm />} />
                <Route path="products/:id" element={<AdminProductForm />} />
                <Route path="categories" element={<AdminCategories />} />
                <Route path="gallery" element={<AdminGallery />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
                <Route path="faqs" element={<AdminFaqs />} />
                <Route path="content" element={<AdminContent />} />
                <Route path="landing-editor" element={<AdminLandingEditor />} />
                <Route path="branding" element={<AdminBranding />} />
                <Route path="enquiries" element={<AdminEnquiries />} />
                <Route path="admins" element={<AdminAdmins />} />
                <Route path="settings" element={<AdminSettings />} />
              </Route>
              <Route path="/404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
            <Toaster richColors position="top-center" />
          </BrowserRouter>
        </BasketProvider>
      </SiteProvider>
    </ThemeProvider>
  );
}

export default App;
